import posthog from 'posthog-js';
import Cookies from 'js-cookie';

// Marketing site analytics, gated on the cookie banner's consent
// (CookieBanner in src/utils/misc.js records its choice in the
// cookie-feedback-given-tas / cookie-feedback-tas cookies).
//
// Consent model ("soft gate", per PostHog's documented banner pattern,
// posthog-js >= 1.424):
// - posthog-js loads immediately. capture() itself is never consent-gated:
//   the consent state only chooses HOW events are counted.
//   * Pending (no banner answer): opt_out_capturing_by_default puts the
//     visitor in anonymous counting — cookieless_mode: 'on_reject' +
//     the project's "Cookieless server hash mode" setting count them via
//     a server-side hash. Nothing stored on the device, no identity.
//   * Accepted: opt_in_capturing() switches to cookie-based tracking.
//   * Rejected: opt_out_capturing() — still counted, cookielessly (only
//     the consent-choice cookie itself is written, like any CMP).
// - $pageview is sent from onRouteUpdate for everyone; the banner's answer
//   is applied on the next syncConsent() (init or any route change), so a
//   landing-page view is never lost, whichever button is clicked.
// - DNT visitors: PostHog never loads, they are never counted at all.
//
// Cookieless counting caveats (accepted trade-off): no GeoIP for
// non-accepters (they don't appear on the world map) and their daily
// uniques run slightly hot (hash salt rotates daily).
//
// Events are tagged site=marketing so they can be filtered apart from the
// admin dashboard in the same PostHog project (admin events carry no tag).
// The tag is passed per event too: persistence (and with it register'd
// super properties) stays off until acceptance.
//
// Single instance only (cf. TAK-13 double-init in admin): no init snippet
// in HTML/SSR alongside this one.

const CONSENT_COOKIE = 'cookie-feedback-tas';

let consentApplied = false;
let lastCapture = { path: null, at: 0 };

const isProductionHost = () =>
    typeof window !== 'undefined' &&
    (window.location.hostname === 'takeaseat.io' ||
        window.location.hostname === 'www.takeaseat.io');

const hasAcceptedCookies = () => Cookies.get(CONSENT_COOKIE) === 'true';

const syncConsent = () => {
    if (consentApplied) return;
    if (hasAcceptedCookies()) {
        posthog.opt_in_capturing();
        posthog.register({ site: 'marketing' }); // re-register: storage was off pre-consent
        consentApplied = true;
    } else if (Cookies.get(CONSENT_COOKIE) === 'false') {
        posthog.opt_out_capturing(); // keep counting, cookielessly
        consentApplied = true;
    }
    // No answer yet: stay in anonymous counting (see block comment).
};

const capturePageview = (path) => {
    // Gatsby fires onRouteUpdate for the initial load right after
    // onClientEntry; same path within 2s is that double fire, not a view.
    const now = Date.now();
    if (path === lastCapture.path && now - lastCapture.at < 2000) return;
    lastCapture = { path, at: now };
    posthog.capture('$pageview', { path, site: 'marketing' });
};

export const onClientEntry = () => {
    if (!isProductionHost()) return;
    // DNT visitors: don't even load PostHog, so the anonymous server-side
    // count never sees them either.
    if (navigator.doNotTrack === '1') return;
    posthog.init('phc_1NUABBjtoBfQVsWkE6AxAdMwBMn9FCVFYt3LTOBKpjO', {
        api_host: 'https://eu.posthog.com',
        // $pageview is sent from onRouteUpdate instead, so SPA navigations count.
        capture_pageview: false,
        capture_exceptions: true,
        respect_dnt: true,
        // Consent gating — see block comment above.
        opt_out_capturing_by_default: true,
        cookieless_mode: 'on_reject',
        person_profiles: 'identified only',
    });
    posthog.register({ site: 'marketing' });
    syncConsent();
};

export const onRouteUpdate = ({ location }) => {
    if (!isProductionHost()) return;
    syncConsent();
    capturePageview(location.pathname);
};
